<script setup lang="ts">
import ChartDisplayCard from '@/components/calculation-result/ChartDisplayCard.vue'
import ChartExportDialog from '@/components/calculation-result/ChartExportDialog.vue'
import ChartFilterPanel from '@/components/calculation-result/ChartFilterPanel.vue'
import ChartListView from '@/components/calculation-result/ChartListView.vue'
import EditSourceRowsDialog from '@/components/calculation-result/EditSourceRowsDialog.vue'
import PivotChartDialog from '@/components/calculation-result/PivotChartDialog.vue'
import ResultToolbar from '@/components/calculation-result/ResultToolbar.vue'
import { useCalculationResultView } from '@/views/calculations/composables/useCalculationResultView'
import { NEW_CHART_CONFIGURATION_ID } from '@/stores/chartConfiguration'

const {
  calculationStore,
  calculationId,
  canvasRef,
  exportDialog,
  exportFileTypes,
  filterItems,
  isFilterActive,
  toggleFilter,
  trajectLabels,
  groupFilterLabel,
  isGroupFilterActive,
  toggleGroupFilter,
  phaseFilterOptions,
  isPhaseActive,
  togglePhase,
  sortOrder,
  sortOrderOptions,
  setSortOrder,
  confirmReset,
  navigateToInput,
  navigateBackToProject,
  isLoading,
  // Chart list
  chartConfigItems,
  showListView,
  activeConfig,
  chartConfiguration,
  openChart,
  backToList,
  // Pivot dialog
  pivotDialogOpen,
  pivotDialogConfig,
  openCreateChartDialog,
  openEditChartDialog,
  savePivotDialog,
  deleteChart,
  openChartExportDialog,
  downloadChartFromDialog,
  materialCategories,
  totalMkiLabel,
  editSourceRowsOpen,
  openEditSourceRows,
  onSourceRowsSaved
} = useCalculationResultView()
</script>

<template>
  <div style="display: flex; flex-direction: column" class="fill-height">
    <template v-if="isLoading">
      <div class="d-flex justify-center align-center fill-height pa-6">
        <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
      </div>
    </template>
    <template v-else>
      <v-container fluid>
        <!-- TITLE ------------------------------------------------------------ -->
        <v-row>
          <v-col cols="12" class="text-center mb-4">
            <h1 class="text-h5 mb-1">
              Results for {{ calculationStore.currentItem?.name }}
              <v-icon color="amber-darken-2" class="ml-1" size="small"> mdi-star </v-icon>
            </h1>
          </v-col>
        </v-row>

        <!-- TOP TOOLBAR ------------------------------------------------------ -->
        <ResultToolbar
          :show-list-view="showListView"
          :chart-count="chartConfigItems.length"
          :sort-order="sortOrder"
          :sort-order-options="sortOrderOptions"
          :phase-filter-options="phaseFilterOptions"
          :is-phase-active="isPhaseActive"
          @back-to-list="backToList"
          @create-chart="openCreateChartDialog"
          @back-to-project="navigateBackToProject"
          @view-input="navigateToInput"
          @edit-source-rows="openEditSourceRows"
          @reset-calculation="confirmReset"
          @export-chart="activeConfig && openChartExportDialog(activeConfig)"
          @set-sort-order="setSortOrder"
          @toggle-phase="togglePhase"
        />

        <!-- LIST VIEW -------------------------------------------------------- -->
        <v-row v-if="showListView">
          <v-col cols="12">
            <ChartListView
              :chart-configurations="chartConfigItems"
              @select="
                (config) =>
                  config.id === NEW_CHART_CONFIGURATION_ID
                    ? openCreateChartDialog()
                    : openChart(config)
              "
              @delete="deleteChart"
              @download="openChartExportDialog"
            />
          </v-col>
        </v-row>

        <!-- CHART VIEW ------------------------------------------------------- -->
        <v-row v-else>
          <v-col cols="12" md="auto" class="chart-filter-col">
            <ChartFilterPanel
              :filter-items="filterItems"
              :is-filter-active="isFilterActive"
              :traject-labels="trajectLabels"
              :group-filter-label="groupFilterLabel"
              :is-group-filter-active="isGroupFilterActive"
              @toggle-filter="toggleFilter"
              @toggle-group-filter="toggleGroupFilter"
            />
          </v-col>

          <v-col cols="12" md="auto" class="chart-display-col">
            <ChartDisplayCard
              :title="activeConfig?.name ?? 'Chart'"
              :total-mki-label="totalMkiLabel"
              :can-delete="chartConfigItems.length > 1"
              @edit="openEditChartDialog"
              @delete="activeConfig && deleteChart(activeConfig)"
            >
              <canvas ref="canvasRef"></canvas>
            </ChartDisplayCard>
          </v-col>
        </v-row>

        <!-- PIVOT DIALOG ----------------------------------------------------- -->
        <PivotChartDialog
          v-model="pivotDialogOpen"
          :chart-configuration="pivotDialogConfig"
          :available-categories="materialCategories"
          @save="savePivotDialog"
        />

        <!-- MODIFY LABEL DIALOG ------------------------------------------- -->
        <EditSourceRowsDialog
          v-model="editSourceRowsOpen"
          :calculation-id="calculationId"
          :chart-configuration="chartConfiguration"
          @saved="onSourceRowsSaved"
        />

        <ChartExportDialog
          v-model="exportDialog"
          :file-types="exportFileTypes"
          @download="downloadChartFromDialog"
        />
      </v-container>
    </template>
  </div>
</template>

<style scoped>
/* A checkbox list needs a modest fixed width; the plot should get everything else. The old
   `lg="2"` ratio handed the panel 320px on a 1920px screen. */
@media (min-width: 960px) {
  .chart-filter-col {
    width: 220px;
  }

  .chart-display-col {
    flex: 1 1 0;
    min-width: 0;
  }
}
</style>
